import api from "./api";
import { downloadFile, downloadCsvFile } from "../utils/exportHelper";

/**
 * Fetch CAG Configurations
 * @param {Object} params - { search, carrierCodes, groupNumbers, programIds, drugListIds, sortBy, sortDirection }
 * @param {number} page - Page number
 * @param {number} size - Page size
 * @returns {Promise} Axios response
 */
export const getCagMappings = (
  params,
  page = 0,
  size = 30,
  cgPlusOnly = false,
) => {
  let url = `/cag-mappings?page=${page}&size=${size}`;
  if (cgPlusOnly) {
    url += "&cg_plus_only=true";
  }
  return api.post(url, params);
};

/**
 * Fetch a specific CAG Configuration Detail
 * @param {number|string} cagId
 * @returns {Promise} Axios response
 */
export const getCagConfiguration = (cagId) => {
  const url = cagId
    ? `/cag-mappings/cag-configuration?cagId=${cagId}`
    : `/cag-mappings/cag-configuration`;
  return api.get(url);
};

/**
 * Create CAG Configuration
 * @param {Object} data - { cagDetail, programConfigs }
 * @returns {Promise} Axios response
 */
export const createCagMapping = (data) => {
  return api.post(`/cag-mappings/cag-configuration`, data);
};

/**
 * Update CAG Configuration
 * @param {number|string} cagId
 * @param {Object} data - { cagDetail, programConfigs }
 * @returns {Promise} Axios response
 */
export const updateCagMapping = (cagId, data) => {
  return api.put(`/cag-mappings/cag-configuration/${cagId}`, data);
};

/**
 * Fetch CAG Filter Options (Cascading)
 * @param {Object} data - { field, searchTerm, filters }
 * @param {number} page - Page number
 * @param {number} size - Page size
 * @returns {Promise} Axios response
 */
export const fetchCagFilterOptions = (data) => {
  return api.get(`/cag-mappings/hierarchy`);
};

/**
 * Fetch Drug List options
 * @param {string} search - Search term
 * @returns {Promise} Axios response
 */
export const fetchDrugLists = (search = "") => {
  return api.get(
    search
      ? `/cag-mappings/drugList?search=${search}`
      : "/cag-mappings/drugList",
  );
};
/**
 * Fetch CAG groups using specific filtering criteria
 * @param {Object} payload - { carrierCodes, searchTerm }
 * @param {number} page - Page number
 * @param {number} size - Page size
 * @returns {Promise} Axios response
 */
export const fetchCagGroupsSearch = (payload) => {
  return api.post(`/cag-mappings/groups/search`, payload);
};

/**
 * Export CAG configurations to CSV using the exportHelper.
 * @param {Object} params - CagMappingRequestDto filters
 * @param {boolean} cgPlusOnly - Filter flag for CG+ configuration
 * @returns {Promise<void>}
 */
export const exportCagMappings = (params, cgPlusOnly = false) => {
  return downloadCsvFile({
    url: "/cag-mappings/export",
    method: "POST",
    payload: params,
    queryParams: cgPlusOnly ? { cg_plus_only: true } : {},
  });
};

/**
 * Fetch CAG Staging Upload Rows
 * @param {string} filter - 'bad' | 'good' | 'duplicate'
 * @returns {Promise} Axios response
 */
export const fetchCagUploadRows = (filter = "bad") => {
  return api.get(`/api/cag/uploads/rows`, {
    params: { filter: filter },
  });
};

/**
 * Delete CAG Staging Upload Rows
 * @param {Array<number|string>} rowIds - Array of row IDs to delete
 * @returns {Promise} Axios response
 */
export const deleteCagUploadRows = (rowIds = []) => {
  return api.delete(`/api/cag/uploads/rows`, {
    data: { rowIds },
  });
};

/**
 * Update CAG Staging Upload Row(s)
 * Endpoint: PUT /api/cag/uploads/rows
 * Payload format: { rows: [ { id, orgCode, orgName, carrierName, carrierId, ... } ] }
 * @param {Object|Array} data - Payload object with rows array or array of row objects or single row
 * @returns {Promise} Axios response
 */
const toUpdatePayload = (data) => {
  if (Array.isArray(data?.rows)) return data;
  if (Array.isArray(data)) return { rows: data };
  if (typeof data === "object" && data !== null) return { rows: [data] };
  return { rows: [] };
};

export const updateCagUploadRow = (data) => {
  return api.put(`/api/cag/uploads/rows`, toUpdatePayload(data));
};

/**
 * Commit CAG Staging Upload Rows (Publish to production)
 * Endpoint: POST /api/cag/uploads/commit
 * Payload format: { rowIds: [ 1, 2 ] }
 * @param {Array<number|string>|Object} data - Array of row IDs or object { rowIds: [...] }
 * @returns {Promise} Axios response
 */
const toRowIds = (data) => {
  if (Array.isArray(data?.rowIds)) return data.rowIds;
  if (Array.isArray(data)) return data;
  return [];
};

export const commitCagUploadRows = (data) => {
  const rowIds = toRowIds(data);
  return api.post(`/api/cag/uploads/commit`, { rowIds });
};

/**
 * Download CAG Staging Upload Template
 * Endpoint: GET /api/cag/uploads/template
 * @returns {Promise<void>}
 */
export const downloadCagTemplate = () => {
  return downloadFile({
    url: "/api/cag/uploads/template",
    method: "GET",
    filename: "cag-config-template.xlsx",
    fileType: "excel",
  });
};

/**
 * Upload CAG file (CSV or Excel) to staging environment
 * Endpoint: POST /api/cag/uploads
 * @param {File|Blob} file - The file to upload
 * @returns {Promise} Axios response
 */
export const uploadCagFile = (file) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post(`/api/cag/uploads`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

/**
 * Fetch CAG Upload History
 * Endpoint: GET /api/cag/uploads
 * @returns {Promise} Axios response
 */
export const fetchCagUploadHistory = () => {
  return api.get(`/api/cag/uploads`);
};

/**
 * Rollback CAG Staging to a specific upload version
 * Endpoint: POST /api/cag/uploads/{version}/rollback
 * @param {string} version - Version identifier (e.g. 'version-1', 'version-2')
 * @returns {Promise} Axios response
 */
export const rollbackCagUploadVersion = (version) => {
  return api.post(`/api/cag/uploads/${encodeURIComponent(version)}/rollback`);
};

export const resetCagUploadVersion = rollbackCagUploadVersion;

/**
 * Download CAG upload file by version
 * Endpoint: GET /api/cag/uploads/{version}/download
 * @param {string} version - Upload version identifier (e.g. 'version-1', 'version-2')
 * @param {string} [filename="cag_upload.xlsx"] - Fallback filename
 * @returns {Promise<void>}
 */
export const downloadCagUploadVersion = (
  version,
  filename = "cag_upload.xlsx",
) => {
  return downloadFile({
    url: `/api/cag/uploads/${encodeURIComponent(version)}/download`,
    method: "GET",
    filename,
    fileType: "excel",
  });
};

/**
 * Download an uploaded file from blob storage via the download API
 * Endpoint: GET /api/cag/uploads/download?blobUrl={blobUrl}
 * @param {string} blobUrl - Azure Blob Storage URL of the uploaded file
 * @param {string} [filename="cag_upload.xlsx"] - Fallback filename
 * @returns {Promise<void>}
 */
export const downloadCagUploadFile = (
  blobUrl,
  filename = "cag_upload.xlsx",
) => {
  return downloadFile({
    url: `/api/cag/uploads/download`,
    method: "GET",
    queryParams: { blobUrl },
    filename,
    fileType: "excel",
  });
};

export const downloadCagUploadVersionFile = (versionOrUrl, filename) => {
  if (typeof versionOrUrl === "string" && versionOrUrl.startsWith("http")) {
    return downloadCagUploadFile(versionOrUrl, filename);
  }
  return downloadCagUploadVersion(versionOrUrl, filename);
};
